<template>
    <div class="auth-wrapper auth-v1">
        <div class="row w-100">
            <Overlay
                :show="showLoading"
                class="col-md-4 col-lg-4 offset-md-4 offset-lg-4"
            >
                <b-card class="mb-0">
                    <b-link class="brand-logo">
                        <vuexy-logo />
                        <img
                            :src="logo"
                            style="max-width: 160px"
                        >
                    </b-link>

                    <div>
                        <h4 class="text-center">
                            {{ $t('Page(MFA):Component(Text):label') }}
                        </h4>
                        <p
                            v-if="isCodeSent"
                            class="pt-1 text-center"
                        >
                            {{ $t('Page(MFA):Component(Text):code-sent-instruction', { method: methodLabel, contact }) }}
                        </p>
                        <p
                            v-else
                            class="pt-1 text-center"
                        >
                            {{ $t('Page(MFA):Component(Text):request-code-instruction', { method: methodLabel, contact }) }}
                        </p>
                    </div>

                    <b-form-group
                        v-if="isCodeSent"
                        label-for="mfa-code"
                        :label="$t('Component(Label):code')"
                    >
                        <b-input-group>
                            <b-form-input
                                id="mfa-code"
                                v-model="code"
                                :placeholder="$t('Component(Placeholder):code')"
                                autofocus
                                @keyup.enter="verifyCode"
                            />
                            <b-input-group-append>
                                <Button @click="verifyCode">
                                    {{ $t('Component(Button):verify') }}
                                </Button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>

                    <small
                        v-if="showChallengeError"
                        class="d-block text-danger mb-1"
                    >
                        {{ $t('Page(MFA):Component(Text):challenge-failed') }}
                    </small>
                    <small
                        v-if="remainingSendAttempts !== null"
                        class="d-block text-muted mb-1"
                    >
                        {{ $t('Page(MFA):Component(Text):remaining-send-attempts', { attempts: remainingSendAttempts }) }}
                    </small>

                    <Button
                        class="btn-block"
                        :disabled="!canRequestCode"
                        @click="requestCode"
                    >
                        {{ $t(isCodeSent ? 'Component(Button):resend-code' : 'Component(Button):send-code') }}
                        <span
                            v-if="resendSeconds > 0"
                            class="ml-1"
                        >({{ resendSeconds }})</span>
                    </Button>

                    <div class="text-center pt-1">
                        <a
                            v-if="hasMultipleMethods"
                            class="text-primary text-decoration-underline"
                            @click="selectAnotherMethod"
                        >
                            {{ $t('Page(MFA):Component(Link):choose-another-method') }}
                        </a>
                        <a
                            class="text-primary text-decoration-underline"
                            :class="{'ml-1': hasMultipleMethods}"
                            @click="backToLogin"
                        >
                            {{ $t('Page(MFA):Component(Link):back-to-login') }}
                        </a>
                    </div>
                </b-card>

                <div class="text-center mt-1">
                    <PrivacyPolicyLink />
                </div>
            </Overlay>
        </div>
    </div>
</template>

<script>
import VuexyLogo from '@core/layouts/components/Logo.vue'
import { Button } from '@core/components/common'
import Overlay from '@core/components/common/Overlay.vue'
import {
    BCard,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BLink,
} from 'bootstrap-vue'
import { UserApi } from '@core/api'
import PrivacyPolicyLink from '@core/components/common/PrivacyPolicyLink.vue'
import useJwt from '@/auth/jwt/useJwt'
import {
    clearMfaState,
    getMfaState,
    saveMfaChallenge,
    selectMfaMethod,
} from '@/auth/mfa'
import { $themeConfig } from '@themeConfig'

export default {
    components: {
        BCard,
        BFormGroup,
        BFormInput,
        BInputGroup,
        BInputGroupAppend,
        BLink,
        Button,
        Overlay,
        PrivacyPolicyLink,
        VuexyLogo,
    },
    props: {
        method: {
            type: String,
            required: true,
        },
        autoSend: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            code: '',
            challenge: null,
            methodData: null,
            showChallengeError: false,
            showLoading: false,
            now: Date.now(),
            timer: null,
            logo: $themeConfig.app.brandingLogo,
        }
    },
    computed: {
        contact() {
            if (!this.methodData || !this.methodData.details) {
                return ''
            }

            return this.method === 'email'
                ? this.methodData.details.email
                : this.methodData.details.phone
        },
        methodLabel() {
            return this.$t(`Page(MFA):Component(Text):method-${this.method}`)
        },
        hasMultipleMethods() {
            const state = getMfaState()
            return state && state.methods.length > 1
        },
        isCodeSent() {
            return this.challenge !== null
        },
        remainingSendAttempts() {
            if (!this.challenge || typeof this.challenge.remainingSendAttempts !== 'number') {
                return null
            }

            return this.challenge.remainingSendAttempts
        },
        resendSeconds() {
            if (!this.challenge || !this.challenge.resendAvailableAt) {
                return 0
            }

            return Math.max(0, Math.ceil((new Date(this.challenge.resendAvailableAt).getTime() - this.now) / 1000))
        },
        canRequestCode() {
            return !this.showLoading
                && this.remainingSendAttempts !== 0
                && this.resendSeconds === 0
        },
    },
    created() {
        const state = getMfaState()

        if (!state || !useJwt.hasToken()) {
            this.redirectToLogin()
            return
        }

        this.methodData = state.methods.find(method => method.type === this.method)

        if (!this.methodData) {
            this.redirectToLogin()
            return
        }

        selectMfaMethod(this.method)
        this.challenge = state.challenges && state.challenges[this.method]
            ? state.challenges[this.method]
            : null
        this.startTimer()

        if (this.autoSend && !this.challenge) {
            this.requestCode()
        }
    },
    beforeDestroy() {
        if (this.timer) {
            clearInterval(this.timer)
        }
    },
    methods: {
        requestCode() {
            this.showLoading = true
            this.showChallengeError = false

            UserApi
                .requestMfaCode(this.method)
                .then(response => {
                    this.now = Date.now()
                    this.challenge = response.data
                    saveMfaChallenge(this.method, response.data)
                    this.startTimer()
                })
                .catch(() => {
                    this.showChallengeError = true
                })
                .finally(() => {
                    this.showLoading = false
                })
        },
        verifyCode() {
            if (!this.code) {
                return
            }

            this.showLoading = true
            this.showChallengeError = false

            UserApi
                .verifyMfaCode(this.code)
                .then(response => {
                    if (!response.data.verified) {
                        this.showChallengeError = true
                        return
                    }

                    clearMfaState()
                    this.$store.commit('setOrganizations', response.data.organizations)
                    this.$router.push({ name: 'select-organization' })
                })
                .catch(() => {
                    this.showChallengeError = true
                })
                .finally(() => {
                    this.showLoading = false
                })
        },
        startTimer() {
            if (this.timer || this.resendSeconds === 0) {
                return
            }

            this.timer = setInterval(() => {
                this.now = Date.now()

                if (this.resendSeconds === 0) {
                    clearInterval(this.timer)
                    this.timer = null
                }
            }, 1000)
        },
        selectAnotherMethod() {
            this.$router.push({ name: 'mfa-method-select' })
        },
        backToLogin() {
            clearMfaState()
            useJwt.clear()
            this.$router.push({ name: 'auth-login' })
        },
        redirectToLogin() {
            clearMfaState()
            useJwt.clear()
            this.$router.replace({ name: 'auth-login' })
        },
    },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
